import {
   GeoJSONFeature,
   LayerSpecification,
   Point,
   Popup,
   SourceSpecification
} from 'mapbox-gl'
import React, { createContext, PropsWithChildren, useContext } from 'react'

import { MapCoordinates, MapStyleEnum } from '../types'

export interface MapsInstanceContextType {
   mapStyle: MapStyleEnum
   zoomValue: number
   isEditMode: boolean
   setIsEditMode: (value: boolean) => void
   onChangeMapStyle: (mapStyle: MapStyleEnum) => void
   onChangeZoomValue: (zoomValue: number) => void
   panTo: (coordinates: MapCoordinates) => void
   fitBounds: (
      coordinatesList: MapCoordinates[],
      shouldAnimate?: boolean
   ) => void
   registerOnClick: (
      onClick: (coordinates: MapCoordinates, point?: Point) => void
   ) => void
   unRegisterOnClick: () => void

   streetViewLayer?: google.maps.StreetViewPanorama
   isStylesLoaded?: boolean
   addSource?: (sourceId: string, source: SourceSpecification) => void
   removeSource?: (sourceId: string) => void
   addLayer?: (layer: LayerSpecification) => void
   removeLayer?: (layerId: string) => void
   getFeaturesForPoint?: (point: Point, layers?: string[]) => GeoJSONFeature[]
   addPopupToMap?: (popup: Popup) => void
}

const MapsInstanceContext = createContext<MapsInstanceContextType | null>(null)

interface MapsInstanceContextProviderProps
   extends MapsInstanceContextType,
      PropsWithChildren {}

export const MapsInstanceContextProvider = (
   props: MapsInstanceContextProviderProps
): React.ReactElement => {
   const { children, ...contextProps } = props

   return (
      <MapsInstanceContext.Provider value={contextProps}>
         {children}
      </MapsInstanceContext.Provider>
   )
}

export const useMapsInstanceContext = (): MapsInstanceContextType => {
   const mapsInstanceContext = useContext(MapsInstanceContext)

   if (!mapsInstanceContext) {
      throw new Error('Expected to be used within MapsContextProvider')
   }

   return mapsInstanceContext
}
